import { __assign, __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import classnames from 'classnames';
import capitalize from 'lodash/capitalize';
import CSSTransition from 'react-transition-group/CSSTransition';
import ClickAwayListener from '../ClickAwayListener';
import { Root, SnackbarContent } from './Snackbar.styles';
var Snackbar = React.forwardRef(function (props, ref) {
  var open = props.open,
      _a = props.anchorOrigin,
      _b = _a === void 0 ? {
    vertical: 'bottom',
    horizontal: 'center'
  } : _a,
      vertical = _b.vertical,
      horizontal = _b.horizontal,
      autoHideDuration = props.autoHideDuration,
      resumeHideDuration = props.resumeHideDuration,
      icon = props.icon,
      action = props.action,
      message = props.message,
      _c = props.withCopy,
      withCopy = _c === void 0 ? true : _c,
      children = props.children,
      className = props.className,
      clickAwayListenerProps = props.ClickAwayListenerProps,
      ContentProps = props.ContentProps,
      _d = props.disableWindowBlurListener,
      disableWindowBlurListener = _d === void 0 ? false : _d,
      onClose = props.onClose,
      onMouseEnter = props.onMouseEnter,
      onMouseLeave = props.onMouseLeave,
      TransitionProps = props.TransitionProps,
      other = __rest(props, ["open", "anchorOrigin", "autoHideDuration", "resumeHideDuration", "icon", "action", "message", "withCopy", "children", "className", "ClickAwayListenerProps", "ContentProps", "disableWindowBlurListener", "onClose", "onMouseEnter", "onMouseLeave", "TransitionProps"]);

  var timeout = (TransitionProps === null || TransitionProps === void 0 ? void 0 : TransitionProps.timeout) || {
    enter: 225,
    exit: 195
  };
  var onEnter = TransitionProps === null || TransitionProps === void 0 ? void 0 : TransitionProps.onEnter;
  var onExited = TransitionProps === null || TransitionProps === void 0 ? void 0 : TransitionProps.onExited;
  var contentRef = React.useRef();
  var timerAutoHide = React.useRef();

  var _e = React.useState(true),
      exited = _e[0],
      setExited = _e[1]; // Timer that controls delay before snackbar auto hides


  var setAutoHideTimer = React.useCallback(function (autoHideDurationParam) {
    var autoHideDurationBefore = autoHideDurationParam != null ? autoHideDurationParam : autoHideDuration;

    if (!onClose || autoHideDurationBefore == null) {
      return;
    }

    clearTimeout(timerAutoHide.current);
    timerAutoHide.current = setTimeout(function () {
      var autoHideDurationAfter = autoHideDurationParam != null ? autoHideDurationParam : autoHideDuration;

      if (!onClose || autoHideDurationAfter == null) {
        return;
      }

      onClose(null, 'timeout');
    }, autoHideDurationBefore);
  }, [autoHideDuration, onClose]);
  React.useEffect(function () {
    if (open) {
      setAutoHideTimer();
    }

    return function () {
      clearTimeout(timerAutoHide.current);
    };
  }, [open, setAutoHideTimer]); // Pause the timer when the user is interacting with the Snackbar
  // or when the user hide the window.

  var handlePause = React.useCallback(function () {
    clearTimeout(timerAutoHide.current);
  }, []); // Restart the timer when the user is no longer interacting with the Snackbar
  // or when the window is shown back.

  var handleResume = React.useCallback(function () {
    if (autoHideDuration != null) {
      if (resumeHideDuration != null) {
        setAutoHideTimer(resumeHideDuration);
        return;
      }

      setAutoHideTimer(autoHideDuration * 0.5);
    }
  }, [autoHideDuration, resumeHideDuration, setAutoHideTimer]);
  var handleMouseEnter = React.useCallback(function (event) {
    if (onMouseEnter) {
      onMouseEnter(event);
    }

    handlePause();
  }, [onMouseEnter, handlePause]);
  var handleMouseLeave = React.useCallback(function (event) {
    if (onMouseLeave) {
      onMouseLeave(event);
    }

    handleResume();
  }, [onMouseLeave, handleResume]);
  var handleClickAway = React.useCallback(function (event) {
    if (onClose) {
      onClose(event, 'clickaway');
    }
  }, [onClose]);
  var handleEnter = React.useCallback(function (node, isAppearing) {
    setExited(false);
    onEnter === null || onEnter === void 0 ? void 0 : onEnter(isAppearing);
  }, [onEnter]);
  var handleExited = React.useCallback(function () {
    setExited(true);
    onExited === null || onExited === void 0 ? void 0 : onExited();
  }, [onExited]);
  React.useEffect(function () {
    if (!disableWindowBlurListener && open) {
      window.addEventListener('focus', handleResume);
      window.addEventListener('blur', handlePause);
      return function () {
        window.removeEventListener('focus', handleResume);
        window.removeEventListener('blur', handlePause);
      };
    }

    return undefined;
  }, [disableWindowBlurListener, handleResume, handlePause, open]);

  if (!open && exited) {
    return null;
  }

  return _jsx(ClickAwayListener, __assign({
    onClickAway: handleClickAway
  }, clickAwayListenerProps, {
    children: _jsx(Root, __assign({
      className: classnames("anchorOrigin".concat(capitalize(vertical)).concat(capitalize(horizontal)), className),
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ref: ref
    }, other, {
      children: _jsx(CSSTransition, __assign({
        appear: true,
        in: open,
        timeout: timeout,
        classNames: "slide"
      }, TransitionProps, {
        onEnter: handleEnter,
        onExited: handleExited,
        nodeRef: contentRef
      }, {
        children: children || _jsx(SnackbarContent, __assign({
          ref: contentRef,
          message: message,
          icon: icon,
          action: action,
          withCopy: withCopy,
          className: vertical === 'top' ? 'directionDown' : 'directionUp'
        }, ContentProps))
      }))
    }))
  }));
});
export default React.memo(Snackbar);